// Local storage utility for managing job applications
export interface StoredApplication {
    id: string;
    name: string;
    email: string;
    phone: string;
    location: string;
    position: string;
    jobId?: string;
    jobTitle?: string;
    experience: string;
    experienceLevel: 'Fresher' | 'Experienced' | '';
    education: string;
    qualification: string;
    specialization: string;
    university: string;
    yearOfPassing: string;
    percentageCgpa: string;
    totalExperience: string;
    previousJobTitle: string;
    currentCtc: string;
    expectedSalary: string;
    noticePeriod: string;
    preferredLocation: string;
    willingToRelocate: string;
    employmentType: string;
    coverLetter: string;
    currentAddress: string;
    linkedinUrl: string;
    portfolioUrl: string;
    dob: string;
    gender: string;
    resumeUrl: string; // NOTE: This should NOT contain Base64 data, only metadata or reference
    resumeFileName?: string;
    status: 'new' | 'reviewing' | 'shortlisted' | 'interviewed' | 'rejected' | 'hired';
    visibility?: 'visible' | 'hidden';
    appliedDate: string;
    rating?: number;
    skills: string[];
}

const STORAGE_KEY = 'amasqis_job_applications';
const MAX_STORAGE_SIZE = 4 * 1024 * 1024; // 4MB safety limit (localStorage is typically 5-10MB)

// Helper to check storage size
const getStorageSize = (): number => {
    let total = 0;
    for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
            total += localStorage[key].length + key.length;
        }
    }
    return total;
};

// Helper to sanitize application data before storage (remove large Base64 data)
const sanitizeForStorage = (application: StoredApplication): StoredApplication => {
    const sanitized = { ...application };
    
    // If resumeUrl is Base64 data (starts with data:), replace with metadata only
    if (sanitized.resumeUrl && sanitized.resumeUrl.startsWith('data:')) {
        // Keep only file name and type, not the actual Base64 data
        sanitized.resumeUrl = `[Resume uploaded: ${sanitized.resumeFileName || 'resume.pdf'}]`;
    }
    
    return sanitized;
};

export const applicationStorage = {
    // Get all applications
    getAll(): StoredApplication[] {
        try {
            const data = localStorage.getItem(STORAGE_KEY);
            return data ? JSON.parse(data) : [];
        } catch (error) {
            console.error('Error reading applications from localStorage:', error);
            return [];
        }
    },

    // Add new application
    add(application: Omit<StoredApplication, 'id' | 'status' | 'appliedDate'>): StoredApplication {
        try {
            const applications = this.getAll();
            const newApplication: StoredApplication = {
                ...application,
                id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                status: 'new',
                appliedDate: new Date().toISOString(),
            };
            
            // Sanitize the application to remove Base64 resume data
            const sanitizedApplication = sanitizeForStorage(newApplication);
            
            applications.unshift(sanitizedApplication); // Add to beginning
            
            // Check storage size before attempting to store
            const dataToStore = JSON.stringify(applications);
            const currentSize = getStorageSize();
            const newDataSize = dataToStore.length;
            
            if (currentSize + newDataSize > MAX_STORAGE_SIZE) {
                console.warn('localStorage quota approaching limit. Removing oldest applications.');
                // Keep only the most recent 50 applications
                const trimmedApplications = applications.slice(0, 50);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedApplications));
                console.log('✅ Stored application (after trimming old data)');
            } else {
                localStorage.setItem(STORAGE_KEY, dataToStore);
                console.log('✅ Stored application in localStorage');
            }
            
            return newApplication; // Return unsanitized version for immediate use
        } catch (error) {
            // Handle QuotaExceededError specifically
            if (error instanceof Error && error.name === 'QuotaExceededError') {
                console.error('❌ localStorage quota exceeded. Application NOT stored locally (but still submitted to server)');
                // Return the application anyway so the form submission can continue
                return {
                    ...application,
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                    status: 'new',
                    appliedDate: new Date().toISOString(),
                };
            }
            console.error('Error adding application to localStorage:', error);
            throw error;
        }
    },

    // Update application
    update(id: string, updates: Partial<StoredApplication>): StoredApplication | null {
        try {
            const applications = this.getAll();
            const index = applications.findIndex(app => app.id === id);
            if (index === -1) return null;
            
            applications[index] = { ...applications[index], ...updates };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(applications));
            return applications[index];
        } catch (error) {
            console.error('Error updating application:', error);
            return null;
        }
    },

    // Delete application
    delete(id: string): boolean {
        try {
            const applications = this.getAll();
            const filtered = applications.filter(app => app.id !== id);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
            return true;
        } catch (error) {
            console.error('Error deleting application:', error);
            return false;
        }
    },

    // Get single application
    getById(id: string): StoredApplication | null {
        const applications = this.getAll();
        return applications.find(app => app.id === id) || null;
    },

    // Clear all applications (useful for testing)
    clear(): void {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing applications:', error);
        }
    }
};